import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import axiosInstance from "../../../axios-instance";
import trueIcon from "../../../assets/images/true-icon.png";
import falseIcon from "../../../assets/images/false-icon.png";
import Loader from "../../../components/Loader/Loader";
import {
  CALENDAR_FEATURE_NAME,
  CALENDAR_FEATURE_NAME_LEGACY,
  comingSoonFeatureLabel,
  isCalendarIntegrationFeature,
  isComingSoonFeature,
  isPooledAvailabilityFeature,
  isStaffConfirmationFeature,
} from "../../../constants/featureAccess";
import {
  formatRoundRobinLimit,
  formatStaffConfirmLimit,
  getPlanDisplayPrice,
  getPlanCalendarText,
  getPlanEventsText,
  CALENDAR_STAFF_REQUEST_HINT,
} from "../../../utils/planLimits";

const FEATURE_SECTIONS = [
  {
    heading: "Scheduling",
    rows: [
      {
        label: "Connected calendars",
        type: "calendar",
        hint: CALENDAR_STAFF_REQUEST_HINT,
      },
      { label: "Events", type: "events" },
      { label: "Round robin events", type: "round_robin" },
      { label: "Staff confirmation events", type: "staff_confirm" },
      { label: "Group Events", feature: "Group Events" },
      { label: "Personalize MeetOcto link", feature: "Personalize MeetOcto link" },
      { label: "Redirect to Custom Pages", feature: "Redirect to Custom Pages" },
    ],
  },
  {
    heading: "Branding & Customization",
    rows: [
      { label: "OctoLink Customization", feature: "OctoLink Customization (colors, images upload)" },
      { label: "Remove MeetOcto Branding", feature: "Removable MeetOcto Branding" },
      { label: "Promotional Banners", feature: "Promotional Banners" },
    ],
  },
  {
    heading: "Notifications & Automation",
    rows: [
      { label: "Automated Event Notifications", feature: "Automated event notifications" },
      { label: "Automated Reminders", feature: "Automated Notifications and Reminders" },
    ],
  },
  {
    heading: "Payments & Commerce",
    rows: [
      { label: "Collect Payments (PayPal, Stripe)", feature: "Collect Payments (PayPal, Stripe)" },
      { label: "Coupon Codes", feature: "Coupon Codes" },
      { label: "Upsell / Cross Sell", feature: "Upsell/ Cross Sell" },
      { label: "Marketing Tools", feature: "Marketing Tools" },
    ],
  },
  {
    heading: "Analytics & Website",
    rows: [
      { label: "Metrics and Reporting", feature: "Metrics and Reporting" },
      { label: "Add OctoLink to Website", feature: "Add OctoLink to your website" },
    ],
  },
  {
    heading: "Integrations",
    rows: [
      { label: "Google Meet", feature: "Google Meet" },
      { label: "Zoom", feature: "Zoom" },
      { label: "Microsoft Teams", feature: "Microsoft Teams" },
      { label: "Zapier", feature: "Zapier", comingSoon: true },
      { label: "Google / Office 365 / Outlook Calendar", feature: CALENDAR_FEATURE_NAME },
    ],
  },
];

function findFeature(plan, featureName) {
  if (!plan?.plan_details) return false;
  const d = plan.plan_details.find(
    (d) =>
      d.feature_name === featureName ||
      (isComingSoonFeature(featureName) && isComingSoonFeature(d.feature_name)) ||
      (isCalendarIntegrationFeature(featureName) &&
        isCalendarIntegrationFeature(d.feature_name)) ||
      (featureName === CALENDAR_FEATURE_NAME &&
        d.feature_name === CALENDAR_FEATURE_NAME_LEGACY)
  );
  return d ? d.status === "E" : false;
}

function getCalendarText(plan) {
  return getPlanCalendarText(plan, { short: true });
}

function getEventsText(plan) {
  return getPlanEventsText(plan, { short: true });
}

function hasPooledAvailability(plan) {
  if (!plan?.plan_details) return false;
  return plan.plan_details.some(
    (d) => isPooledAvailabilityFeature(d.feature_name) && d.status === "E"
  );
}

function getRoundRobinText(plan) {
  return formatRoundRobinLimit(
    plan?.no_of_round_robin_events,
    hasPooledAvailability(plan)
  );
}

function hasStaffConfirmation(plan) {
  if (!plan?.plan_details) return false;
  return plan.plan_details.some(
    (d) => isStaffConfirmationFeature(d.feature_name) && d.status === "E"
  );
}

function getStaffConfirmText(plan) {
  return formatStaffConfirmLimit(
    plan?.no_of_staff_confirm_events,
    hasStaffConfirmation(plan)
  );
}

const PlanComparison = ({ subscription }) => {
  const [plans, setPlans] = useState([]);
  const [period, setPeriod] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const userPlan = subscription?.userPlan || {};

  useEffect(() => {
    axiosInstance
      .get("/admin/plans")
      .then((r) => { setPlans(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filteredPlans = [...plans]
    .sort((a, b) => Number(a.monthly_price) - Number(b.monthly_price));

  const bestIdx = Math.floor(filteredPlans.length / 2);
  const isMonthly = period === "monthly";
  const isCurrent = (plan) => plan?.id === userPlan?.plan_id;

  const handleBuyNow = () => {
    navigate("/admin/manage-subscription");
  };

  const getPrice = (plan) =>
    getPlanDisplayPrice(plan, isMonthly ? "monthly" : "annual");

  return (
    <section className="cmp-page">
      <Loader isShowLoader={loading} />

      {/* Page header */}
      <div className="cmp-page-header">
        <button className="cmp-back-btn" onClick={() => navigate(-1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Back
        </button>
        <h1 className="cmp-title">Full Feature Comparison</h1>
        <p className="cmp-subtitle">See exactly what's included in every plan.</p>

        <div className="cmp-controls">
          <div className="cmp-period-tabs">
            <button className={`cmp-tab${isMonthly ? " active" : ""}`} onClick={() => setPeriod("monthly")}>Monthly</button>
            <button className={`cmp-tab${!isMonthly ? " active" : ""}`} onClick={() => setPeriod("annual")}>
              Annual <span className="cmp-save">Save 20%</span>
            </button>
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="cmp-table-wrap">
        <div className="cmp-table-scroll">
          <table className="cmp-table">
            <thead>
              <tr>
                <th className="cmp-th-label" />
                {filteredPlans.map((plan, idx) => (
                  <th key={plan.id} className={idx === bestIdx ? "cmp-th-featured" : ""}>
                    {idx === bestIdx && <div className="cmp-best-badge">Best Value</div>}
                    <div className="cmp-th-name">{plan.plan_name}</div>
                    <div className="cmp-th-price">
                      <span className="cmp-price-sym">$</span>
                      <span className="cmp-price-val">{getPrice(plan)}</span>
                      {Number(plan.monthly_price) > 0 && (
                        <span className="cmp-price-per">/mo</span>
                      )}
                    </div>
                    {!isMonthly && Number(plan.monthly_price) > 0 && (
                      <div className="cmp-th-billed">Billed annually</div>
                    )}
                    <button
                      className={`cmp-cta${idx === bestIdx ? " cmp-cta--featured" : ""}`}
                      onClick={handleBuyNow}
                      disabled={isCurrent(plan)}
                    >
                      {isCurrent(plan) ? "Current Plan" : Number(plan.monthly_price) === 0 ? "Get Started Free" : "Buy Now"}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_SECTIONS.map((section, si) => (
                <React.Fragment key={si}>
                  <tr className="cmp-section-row">
                    <td colSpan={filteredPlans.length + 1}>{section.heading}</td>
                  </tr>
                  {section.rows.map((row, ri) => (
                    <tr key={ri} className="cmp-feature-row">
                      <td className="cmp-td-label">
                        {row.comingSoon || isComingSoonFeature(row.feature)
                          ? comingSoonFeatureLabel(row.label)
                          : row.label}
                        {row.hint && (
                          <small className="cmp-hint">{row.hint}</small>
                        )}
                      </td>
                      {filteredPlans.map((plan, idx) => {
                        let content;
                        if (row.comingSoon || isComingSoonFeature(row.feature)) {
                          content = <span className="cmp-text">Coming soon</span>;
                        } else if (row.type === "calendar") {
                          content = <span className="cmp-text">{getCalendarText(plan)}</span>;
                        } else if (row.type === "events") {
                          content = <span className="cmp-text">{getEventsText(plan)}</span>;
                        } else if (row.type === "round_robin") {
                          content = <span className="cmp-text">{getRoundRobinText(plan)}</span>;
                        } else if (row.type === "staff_confirm") {
                          content = <span className="cmp-text">{getStaffConfirmText(plan)}</span>;
                        } else {
                          const enabled = findFeature(plan, row.feature);
                          content = <img src={enabled ? trueIcon : falseIcon} className="cmp-icon" alt={enabled ? "yes" : "no"} />;
                        }
                        return (
                          <td key={plan.id} className={idx === bestIdx ? "cmp-td-featured" : ""}>
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="cmp-td-label" />
                {filteredPlans.map((plan, idx) => (
                  <td key={plan.id} className={idx === bestIdx ? "cmp-td-featured" : ""}>
                    <button
                      className={`cmp-cta cmp-cta--sm${idx === bestIdx ? " cmp-cta--featured" : ""}`}
                      onClick={handleBuyNow}
                      disabled={isCurrent(plan)}
                    >
                      {isCurrent(plan) ? "Current Plan" : Number(plan.monthly_price) === 0 ? "Get Started Free" : "Buy Now"}
                    </button>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>
  );
};

const mapStateToProps = (state) => ({ subscription: state.subscription });
export default connect(mapStateToProps)(PlanComparison);
